import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import {
  apartmentReferenceKey,
  isApartmentService,
  type ApartmentAverage,
  type ApartmentRoomKindAverage,
  type ApartmentSteelAverage,
} from '@obraplan/core/domain/takeoff/apartment-average';
import { serviceElement, type AppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import { foundationElementOf } from '@obraplan/core/domain/takeoff/foundation-average';
import type { PieceElement } from '@obraplan/core/domain/takeoff/foundation-factors';
import { structureElementOf } from '@obraplan/core/domain/takeoff/structure-average';
import type { IndexAverage } from '@obraplan/core/domain/takeoff/index-origin';
import type { RoomKindAverage } from '@obraplan/core/domain/takeoff/own-factor';
import type { Room } from '@obraplan/core/domain/takeoff/room';
import { descendsToRooms } from '@obraplan/core/domain/takeoff/room-quantity';
import { serviceLockKey, serviceLockKeyOf } from '@obraplan/core/domain/takeoff/project-budget';
import { referenceItemFor, type ReferenceItem } from '@obraplan/core/domain/takeoff/rate-average';
import type {
  CatalogDiscipline,
  CatalogItem,
  CatalogService,
  ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import {
  readsSteelRate,
  steelElementsOf,
  steelRatesFor,
  type SteelGauge,
  type SteelRateAverage,
} from '@obraplan/core/domain/takeoff/steel';
import { chaveDoItemNovo, comoItemDaMedia, itemDaObra, mesmaMedia } from './trazer-a-media';
import type { WorkspaceAction } from './typology-draft';

/**
 * A média do apartamento chegando à obra (Fase 40, etapa 1 — A4, RN-521, RN-522).
 *
 * ```text
 * a tipologia escolhe CHAPISCO  ──►  GetApartmentAverage  ──►  ADD_SERVICE_ITEM (o que falta)
 *                                                          └─►  APPLY_INDEX_AVERAGE (o índice médio)
 * ```
 *
 * Como a estaca (Fase 39), quem traz é a tela: a bancada grava o catálogo num envelope inteiro, e
 * o item que o servidor acrescentasse por fora sumiria na próxima gravação (ADR-184). Tudo aqui é
 * conta pura sobre o catálogo e a resposta, para a tela e o teste lerem o mesmo.
 *
 * Desde a Fase 41 (etapa 3 — RN-553, RN-554), a mesma mecânica vale para os serviços que medem o
 * bloco, o baldrame e o radier: a média da fundação vem na mesma resposta (`media.foundation`), e
 * o aço dela, por elemento.
 *
 * Desde a Fase 42 (etapa 4 — RN-581), também para os que medem o pilar, a viga, a escada, o muro e
 * o pilar de contenção, e para os do arranque: a média da estrutura vem em `media.structure`, uma
 * por serviço × item (G9), e o aço por elemento (RN-580) — na disciplina que mistura, cada lista
 * de elemento recebe a dele (RN-573).
 */

/** As seis casas da coluna (`0091`): a foto volta igual do banco, e a faixa não acende à toa. */
const seis = (valor: number): number => Math.round(valor * 1e6) / 1e6;

/** A foto que o índice guarda, de uma linha da média e do dia da resposta. */
export function fotoDaMedia(average: ApartmentAverage, at: string): IndexAverage {
  return {
    rate: seis(average.rate),
    at,
    projects: average.projects,
    unitTypes: average.unitTypes,
  };
}

const SEM_TRAVA = (): boolean => false;

/**
 * O serviço tem média: o do apartamento (Fase 40), o que mede bloco, baldrame ou radier (Fase 41,
 * etapa 3 — RN-553), ou o que mede pilar, viga, escada, muro ou pilar de contenção (Fase 42, etapa
 * 4 — RN-581). O do arranque aplica-se a `NENHUM` (G13): já é do apartamento por aí, e a média
 * dele vem na da estrutura, pela chave do serviço. A estaca segue pelo tipo (Fase 39).
 */
export function temMedia(servico: Pick<CatalogService, 'measureBy' | 'appliesTo'>): boolean {
  return (
    isApartmentService(servico) ||
    foundationElementOf(servico) !== undefined ||
    structureElementOf(servico) !== undefined
  );
}

/**
 * As médias da resposta, pela chave da trava do serviço e a do item da referência: as do
 * apartamento, as da fundação (Fase 41, RN-551) e as da estrutura (Fase 42, RN-579) — o serviço é
 * de uma ou de outra.
 *
 * A exceção é o arranque (G13): o serviço dele aplica-se a `NENHUM`, que também é do apartamento
 * (o "por unidade"). Se as duas trouxerem a mesma chave, vale a da estrutura, que vem por último:
 * é ela que lê o arranque peça a peça, pelo kg do corte e dobra.
 */
function mediasPorChave(media: ApartmentAverageForProject): ReadonlyMap<string, ApartmentAverage> {
  return new Map(
    [
      ...media.averages,
      ...(media.foundation?.averages ?? []),
      ...(media.structure?.averages ?? []),
    ].map((average) => [`${average.serviceKey}|${apartmentReferenceKey(average.item)}`, average]),
  );
}

/** Os itens da referência num serviço, pela chave da trava. */
function daReferencia(
  media: ApartmentAverageForProject,
  chaveDaTrava: string,
): readonly ReferenceItem[] {
  return media.referenceItems.filter(
    (ref) => serviceLockKeyOf(ref.disciplineName, ref.serviceName) === chaveDaTrava,
  );
}

/** A média que vale para um item do catálogo da obra, pelo par dele na referência. */
export function mediaDoItem(
  item: CatalogItem,
  servico: Pick<CatalogService, 'key'>,
  media: ApartmentAverageForProject,
): ApartmentAverage | undefined {
  const chaveDaTrava = serviceLockKey(servico.key);
  const par = referenceItemFor(comoItemDaMedia(item), daReferencia(media, chaveDaTrava));
  if (par === undefined) return undefined;
  return mediasPorChave(media).get(`${chaveDaTrava}|${apartmentReferenceKey(par)}`);
}

export interface MediaDoApartamentoTrazida {
  readonly acoes: readonly WorkspaceAction[];
  /** Quantos itens da referência entraram, e quantos com o índice médio. */
  readonly acrescentados: number;
  readonly comMedia: number;
  /** Quantas bitolas ganharam a taxa média do aço — da laje (etapa 3) ou da fundação (Fase 41). */
  readonly taxas: number;
}

/**
 * O que a escolha dos serviços traz da média (RN-521).
 *
 * Só os serviços recém-escolhidos, com média (`temMedia`), e abertos — o aprovado não ganha item nem
 * índice (Fase 33, RN-438). Em cada um:
 *
 * - o item da referência que falta entra, como está na referência (D3), com o índice médio
 *   quando há média, e a perda vazia (D13);
 * - o item que a obra já tem e que está **sem índice** ganha o índice médio;
 * - o item com índice digitado fica, e ganha a média ao lado (a foto);
 * - o item que já segue a média fica: é o "Atualizar" que o troca.
 */
export function trazerAMediaDoApartamento(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  escolhidos: ReadonlySet<string>,
  media: ApartmentAverageForProject,
  travado: (serviceKey: string) => boolean = SEM_TRAVA,
): MediaDoApartamentoTrazida {
  const acoes: WorkspaceAction[] = [];
  let acrescentados = 0;
  let comMedia = 0;
  if (media.reference === undefined) return { acoes, acrescentados, comMedia, taxas: 0 };

  const medias = mediasPorChave(media);
  const servicos = (catalog?.disciplines ?? []).flatMap((disciplina) => disciplina.services);
  for (const servico of servicos) {
    if (!escolhidos.has(servico.key) || !temMedia(servico) || travado(servico.key)) {
      continue;
    }
    const chaveDaTrava = serviceLockKey(servico.key);
    const planejados = new Set<string>();
    for (const ref of daReferencia(media, chaveDaTrava)) {
      const average = medias.get(`${chaveDaTrava}|${apartmentReferenceKey(ref)}`);
      const foto = average === undefined ? undefined : fotoDaMedia(average, media.at);
      const daObra = itemDaObra(servico, ref);
      if (daObra === undefined) {
        const itemKey = chaveDoItemNovo(ref.description);
        if (planejados.has(itemKey)) continue;
        planejados.add(itemKey);
        acoes.push({
          type: 'ADD_SERVICE_ITEM',
          serviceKey: servico.key,
          description: ref.description,
          // A classe da referência é decisão de gente, e não palpite: vai como está lá.
          kind: ref.kind,
          ...(ref.code === undefined ? {} : { code: ref.code }),
          ...(ref.unit === undefined ? {} : { unit: ref.unit }),
          ...(ref.kind === 'MAO_DE_OBRA' && ref.jobRoleId !== undefined
            ? { jobRoleId: ref.jobRoleId }
            : {}),
          ...(ref.steelGauge === undefined ? {} : { steelGauge: ref.steelGauge }),
        });
        acrescentados += 1;
        if (foto !== undefined) {
          acoes.push({
            type: 'APPLY_INDEX_AVERAGE',
            serviceKey: servico.key,
            itemKey,
            average: foto,
            follow: true,
          });
          comMedia += 1;
        }
        continue;
      }
      if (foto === undefined || daObra.indexOrigin === 'MEDIA') continue;
      const semIndice = daObra.consumptionIndex === undefined;
      acoes.push({
        type: 'APPLY_INDEX_AVERAGE',
        serviceKey: servico.key,
        itemKey: daObra.key,
        average: foto,
        follow: semIndice,
      });
      if (semIndice) comMedia += 1;
    }
  }
  const doAco = trazerATaxaDoAco(catalog, escolhidos, media, travado);
  return {
    acoes: [...acoes, ...doAco],
    acrescentados,
    comMedia,
    taxas: doAco.filter((acao) => acao.type === 'APPLY_STEEL_AVERAGE' && acao.follow).length,
  };
}

/** Um índice que seguia a média e que a média de hoje mudou (RN-522). */
export interface MudancaDoIndice {
  readonly serviceKey: string;
  readonly itemKey: string;
  readonly descricao: string;
  readonly de: number;
  readonly para: number;
  /** O dia da foto que mudou, `aaaa-mm-dd`. */
  readonly desde?: string;
}

/**
 * O que segue a média num serviço e mudou, até a quarta casa — a que a tela mostra. O digitado
 * não entra: ele não muda com a média. O item que a média de hoje não tem também não: a foto
 * continua valendo, e trocar por "sem índice" calaria o item.
 */
export function mudancasDoServico(
  servico: CatalogService,
  media: ApartmentAverageForProject,
): readonly MudancaDoIndice[] {
  if (!temMedia(servico)) return [];
  return servico.items.flatMap((item): MudancaDoIndice[] => {
    if (item.indexOrigin !== 'MEDIA' || item.consumptionIndex === undefined) return [];
    const hoje = mediaDoItem(item, servico, media);
    if (hoje === undefined || mesmaMedia(hoje.rate, item.consumptionIndex)) return [];
    return [
      {
        serviceKey: servico.key,
        itemKey: item.key,
        descricao: item.description,
        de: item.consumptionIndex,
        para: seis(hoje.rate),
        ...(item.indexAverage === undefined ? {} : { desde: item.indexAverage.at }),
      },
    ];
  });
}

/**
 * O "Atualizar pela média" de um serviço (D8): o que segue a média passa à média de hoje, e o
 * digitado ganha a média nova ao lado, sem mudar.
 */
export function atualizarServicoPelaMedia(
  servico: CatalogService,
  media: ApartmentAverageForProject,
): readonly WorkspaceAction[] {
  if (!temMedia(servico)) return [];
  return servico.items.flatMap((item): WorkspaceAction[] => {
    if (item.consumptionIndex === undefined) return [];
    const hoje = mediaDoItem(item, servico, media);
    if (hoje === undefined) return [];
    const segue = item.indexOrigin === 'MEDIA';
    if (segue && mesmaMedia(hoje.rate, item.consumptionIndex)) return [];
    return [
      {
        type: 'APPLY_INDEX_AVERAGE',
        serviceKey: servico.key,
        itemKey: item.key,
        average: fotoDaMedia(hoje, media.at),
        follow: segue,
      },
    ];
  });
}

// ── A média do tipo de cômodo (Fase 40, etapa 2 — A2, A3, RN-528, RN-529) ───────

/** A foto que o fator do cômodo guarda, de uma linha da média do tipo. */
function fotoDoTipo(average: ApartmentRoomKindAverage, at: string): RoomKindAverage {
  return {
    rate: seis(average.rate),
    at,
    projects: average.projects,
    rooms: average.rooms,
    roomKind: average.roomKind,
  };
}

/** A média do tipo de cômodo de um item da referência, quando ela é outra que a geral. */
function mediaDoTipoQueVale(
  chaveDaTrava: string,
  ref: ReferenceItem,
  roomKind: Room['kind'],
  media: ApartmentAverageForProject,
): ApartmentRoomKindAverage | undefined {
  if (roomKind === 'OUTRO') return undefined;
  const chave = apartmentReferenceKey(ref);
  const doTipo = media.byRoomKind.find(
    (a) =>
      a.serviceKey === chaveDaTrava &&
      a.roomKind === roomKind &&
      apartmentReferenceKey(a.item) === chave,
  );
  if (doTipo === undefined) return undefined;
  const geral = mediasPorChave(media).get(`${chaveDaTrava}|${chave}`);
  // Igual à geral até a quarta casa, o índice da obra já diz o mesmo: não nasce um próprio à toa.
  return geral !== undefined && mesmaMedia(geral.rate, doTipo.rate) ? undefined : doTipo;
}

/**
 * O fator do tipo nos cômodos (RN-528): cada cômodo de tipo com média ganha o próprio com a média
 * do tipo, quando ela difere da geral.
 *
 * - o próprio digitado fica;
 * - o que veio da média **deste** tipo fica — é o "Atualizar" que o troca (RN-529);
 * - o que veio da média de **outro** tipo (o cômodo mudou de tipo) troca pela média do tipo novo,
 *   ou sai, quando o tipo novo não tem média.
 *
 * Os itens saem da referência (`itemDaObra`, ou a chave que o item novo terá): a escolha do
 * serviço acrescenta os itens no mesmo passo, antes de o catálogo da tela os ter.
 */
export function fatoresDoTipoNosComodos(
  rooms: readonly Pick<Room, 'id' | 'kind' | 'factors' | 'deletedAt'>[],
  servicos: readonly CatalogService[],
  media: ApartmentAverageForProject,
): readonly WorkspaceAction[] {
  if (media.reference === undefined) return [];
  const acoes: WorkspaceAction[] = [];
  for (const servico of servicos) {
    if (!descendsToRooms(servico)) continue;
    const chaveDaTrava = serviceLockKey(servico.key);
    for (const ref of daReferencia(media, chaveDaTrava)) {
      if (ref.steelGauge !== undefined) continue;
      const itemKey = itemDaObra(servico, ref)?.key ?? chaveDoItemNovo(ref.description);
      for (const room of rooms) {
        if (room.deletedAt !== undefined) continue;
        const atual = room.factors?.find(
          (linha) => linha.serviceKey === servico.key && linha.itemKey === itemKey,
        );
        if (atual !== undefined && atual.origin !== 'MEDIA') continue;
        if (atual?.average?.roomKind === room.kind) continue;
        const doTipo = mediaDoTipoQueVale(chaveDaTrava, ref, room.kind, media);
        if (doTipo !== undefined) {
          acoes.push({
            type: 'SET_ROOM_FACTOR',
            roomId: room.id,
            serviceKey: servico.key,
            itemKey,
            factor: seis(doTipo.rate),
            origin: 'MEDIA',
            average: fotoDoTipo(doTipo, media.at),
          });
        } else if (atual !== undefined) {
          acoes.push({
            type: 'SET_ROOM_FACTOR',
            roomId: room.id,
            serviceKey: servico.key,
            itemKey,
          });
        }
      }
    }
  }
  return acoes;
}

/** Um fator do tipo de cômodo que seguia a média e que a média de hoje mudou (RN-529). */
export interface MudancaDoTipo {
  readonly roomKind: Room['kind'];
  readonly itemKey: string;
  readonly descricao: string;
  readonly de: number;
  readonly para: number;
  /** Os cômodos que seguem esta média — o "Atualizar" troca todos. */
  readonly roomIds: readonly Room['id'][];
}

/**
 * O que segue a média do tipo de cômodo num serviço de ambiente e mudou, até a quarta casa, nos
 * cômodos desta tipologia. Uma linha por tipo × item.
 */
export function mudancasDosComodos(
  servico: CatalogService,
  rooms: readonly Pick<Room, 'id' | 'kind' | 'factors' | 'deletedAt'>[],
  media: ApartmentAverageForProject,
): readonly MudancaDoTipo[] {
  if (!descendsToRooms(servico)) return [];
  const chaveDaTrava = serviceLockKey(servico.key);
  const porChave = new Map<string, { -readonly [K in keyof MudancaDoTipo]: MudancaDoTipo[K] }>();
  for (const room of rooms) {
    if (room.deletedAt !== undefined) continue;
    for (const linha of room.factors ?? []) {
      if (linha.serviceKey !== servico.key || linha.origin !== 'MEDIA') continue;
      const item = servico.items.find((i) => i.key === linha.itemKey);
      if (item === undefined) continue;
      const par = referenceItemFor(comoItemDaMedia(item), daReferencia(media, chaveDaTrava));
      if (par === undefined) continue;
      const hoje = media.byRoomKind.find(
        (a) =>
          a.serviceKey === chaveDaTrava &&
          a.roomKind === room.kind &&
          apartmentReferenceKey(a.item) === apartmentReferenceKey(par),
      );
      if (hoje === undefined || mesmaMedia(hoje.rate, linha.factor)) continue;
      const chave = `${room.kind}|${linha.itemKey}`;
      const antes = porChave.get(chave);
      if (antes === undefined) {
        porChave.set(chave, {
          roomKind: room.kind,
          itemKey: linha.itemKey,
          descricao: item.description,
          de: linha.factor,
          para: seis(hoje.rate),
          roomIds: [room.id],
        });
      } else {
        antes.roomIds = [...antes.roomIds, room.id];
      }
    }
  }
  return [...porChave.values()];
}

/** O "Atualizar" da média do tipo (RN-529): o próprio que segue a média passa à de hoje. */
export function atualizarComodosPelaMedia(
  servico: CatalogService,
  rooms: readonly Pick<Room, 'id' | 'kind' | 'factors' | 'deletedAt'>[],
  media: ApartmentAverageForProject,
): readonly WorkspaceAction[] {
  const chaveDaTrava = serviceLockKey(servico.key);
  return mudancasDosComodos(servico, rooms, media).flatMap((mudanca) => {
    const doTipo = media.byRoomKind.find(
      (a) =>
        a.serviceKey === chaveDaTrava &&
        a.roomKind === mudanca.roomKind &&
        seis(a.rate) === mudanca.para,
    );
    if (doTipo === undefined) return [];
    return mudanca.roomIds.map((roomId): WorkspaceAction => ({
      type: 'SET_ROOM_FACTOR',
      roomId,
      serviceKey: servico.key,
      itemKey: mudanca.itemKey,
      factor: mudanca.para,
      origin: 'MEDIA',
      average: fotoDoTipo(doTipo, media.at),
    }));
  });
}

// ── O aço com média: a laje (Fase 40), a fundação (Fase 41) e a estrutura (Fase 42) ──

/** Três casas, como a coluna da taxa (`numeric(12, 3)`): a foto volta igual do banco. */
const tres = (valor: number): number => Math.round(valor * 1000) / 1000;

/**
 * O elemento cujo aço tem média: a laje (RN-532), o bloco, o baldrame e o radier (RN-552), e desde
 * a Fase 42 (etapa 4 — RN-580) o pilar, a viga, a escada, o muro e o pilar de contenção. O arranque
 * não tem volume e não tem média de aço.
 */
export type ElementoDoAco = 'LAJE' | PieceElement;

/** O elemento de um serviço de aço com média, ou `undefined`. */
function elementoDoServicoDeAco(
  servico: Pick<CatalogService, 'measureBy' | 'appliesTo'>,
): ElementoDoAco | undefined {
  if (servico.measureBy !== 'ACO') return undefined;
  if (serviceElement(servico) === 'LAJE') return 'LAJE';
  return foundationElementOf(servico) ?? structureElementOf(servico);
}

/**
 * O elemento cujo aço a disciplina compra: o do primeiro serviço de aço dela que tem média — a
 * laje, a fundação ou a estrutura. É o da lista da disciplina inteira; na disciplina que mistura
 * elementos, cada um segue a média dele na lista dele (Fase 42, `elementoDoAcoDaLista`).
 */
export function elementoDoAco(
  disciplina: Pick<CatalogDiscipline, 'services'>,
): ElementoDoAco | undefined {
  for (const servico of disciplina.services) {
    const elemento = elementoDoServicoDeAco(servico);
    if (elemento !== undefined) return elemento;
  }
  return undefined;
}

/** A disciplina compra um aço com média (a laje; a fundação desde a 41; a estrutura desde a 42). */
export function compraAcoComMedia(disciplina: Pick<CatalogDiscipline, 'services'>): boolean {
  return elementoDoAco(disciplina) !== undefined;
}

/**
 * Os elementos da disciplina que mistura (Fase 42, etapa 3 — RN-573): os que os serviços de aço
 * dela medem, quando são mais de um. Aí a taxa é uma lista por elemento, e a média de cada um vai
 * para a dele. Vazio na disciplina de um elemento só: a taxa dela é uma, como sempre.
 */
export function elementosDaTaxa(
  disciplina: Pick<CatalogDiscipline, 'services'>,
): readonly AppliesTo[] {
  const elementos = steelElementsOf(disciplina.services);
  return elementos.length > 1 ? elementos : [];
}

/**
 * O elemento cuja média do aço uma lista da disciplina segue: a lista da disciplina inteira, o do
 * primeiro serviço de aço com média (`elementoDoAco`); a de um elemento, o próprio — quando ele
 * tem média (a laje, a fundação e, desde a etapa 4, o muro e o pilar de contenção, cada um na
 * lista dele da PILARES DE CONTENÇÃO, RN-580).
 */
export function elementoDoAcoDaLista(
  disciplina: Pick<CatalogDiscipline, 'services'>,
  lista: AppliesTo | undefined,
): ElementoDoAco | undefined {
  if (lista === undefined) return elementoDoAco(disciplina);
  return elementoDoServicoDeAco({ measureBy: 'ACO', appliesTo: lista });
}

/**
 * As listas da disciplina que recebem a média do aço: a da disciplina inteira, na de um elemento
 * só; na que mistura, a de cada elemento com média (RN-573, RN-581).
 */
function listasComMedia(
  disciplina: Pick<CatalogDiscipline, 'services'>,
): readonly { readonly lista?: AppliesTo; readonly elemento: ElementoDoAco }[] {
  const mistura = elementosDaTaxa(disciplina);
  const listas = mistura.length === 0 ? [undefined] : mistura;
  return listas.flatMap((lista) => {
    const elemento = elementoDoAcoDaLista(disciplina, lista);
    if (elemento === undefined) return [];
    return [lista === undefined ? { elemento } : { lista, elemento }];
  });
}

/** A foto que a taxa guarda, de uma linha da média do aço e do dia da resposta. */
export function fotoDaTaxa(
  average: Pick<ApartmentSteelAverage, 'rate' | 'projects'>,
  at: string,
): SteelRateAverage {
  return { rate: tres(average.rate), at, projects: average.projects };
}

/**
 * As médias de hoje do aço de um elemento: a da laje, ou a da fundação ou a da estrutura filtrada
 * por ele (Fase 42, RN-580). Os elementos das duas não se repetem: a fundação é bloco, baldrame e
 * radier; a estrutura, o resto.
 */
function mediasDoAco(
  media: ApartmentAverageForProject,
  elemento: ElementoDoAco,
): readonly ApartmentSteelAverage[] {
  if (elemento === 'LAJE') return media.steel;
  return [...(media.foundation?.steel ?? []), ...(media.structure?.steel ?? [])].filter(
    (average) => average.element === elemento,
  );
}

/** A média de hoje de uma bitola no aço de um elemento — a laje, quando não diz qual. */
export function mediaDaBitola(
  gauge: SteelGauge,
  media: ApartmentAverageForProject,
  elemento: ElementoDoAco = 'LAJE',
): ApartmentSteelAverage | undefined {
  return mediasDoAco(media, elemento).find((average) => average.gauge === gauge);
}

/**
 * O que a escolha de um serviço de aço com média traz (RN-533; Fase 41, RN-553; Fase 42, RN-581),
 * disciplina a disciplina — a média do elemento da disciplina (`elementoDoAco`):
 *
 * - a bitola que a disciplina não tem ganha a taxa média;
 * - a digitada fica, e ganha a média ao lado (a foto);
 * - a que já segue a média fica: é o "Atualizar" que a troca.
 *
 * Na disciplina que mistura elementos (Fase 42, RN-573, RN-581), a média de cada elemento vai para
 * a lista dele, e vem com a escolha do serviço de aço dele. O elemento sem a própria lê a da
 * disciplina: a dele nasce copiando-a, com a média nas bitolas que faltam.
 *
 * A disciplina com serviço de aço aprovado não ganha nada: a taxa dela não muda mais (RN-438).
 */
export function trazerATaxaDoAco(
  catalog: Pick<ServiceCatalog, 'disciplines'> | undefined,
  escolhidos: ReadonlySet<string>,
  media: ApartmentAverageForProject,
  travado: (serviceKey: string) => boolean = SEM_TRAVA,
): readonly WorkspaceAction[] {
  if (media.reference === undefined) return [];
  return (catalog?.disciplines ?? []).flatMap((disciplina): WorkspaceAction[] => {
    if (disciplina.services.some((servico) => readsSteelRate(servico) && travado(servico.key))) {
      return [];
    }
    return listasComMedia(disciplina).flatMap(({ lista, elemento }): WorkspaceAction[] => {
      const escolheu = disciplina.services.some((servico) => {
        if (!escolhidos.has(servico.key)) return false;
        const doServico = elementoDoServicoDeAco(servico);
        return lista === undefined ? doServico !== undefined : doServico === elemento;
      });
      if (!escolheu) return [];
      const taxas = steelRatesFor(disciplina, lista);
      return mediasDoAco(media, elemento).flatMap((average): WorkspaceAction[] => {
        const atual = taxas?.find((taxa) => taxa.gauge === average.gauge);
        if (atual?.origin === 'MEDIA') return [];
        return [
          {
            type: 'APPLY_STEEL_AVERAGE',
            disciplineKey: disciplina.key,
            gauge: average.gauge,
            average: fotoDaTaxa(average, media.at),
            follow: atual === undefined,
            ...(lista === undefined ? {} : { element: lista }),
          },
        ];
      });
    });
  });
}

/** Uma taxa que seguia a média e que a média de hoje mudou (RN-534). */
export interface MudancaDaTaxa {
  readonly gauge: SteelGauge;
  readonly de: number;
  readonly para: number;
  /** O dia da foto que mudou, `aaaa-mm-dd`. */
  readonly desde?: string;
}

/**
 * O que segue a média do aço numa disciplina e mudou, até a terceira casa — a média do elemento
 * dela (a laje, a fundação ou a estrutura). A digitada não entra: ela não muda com a média. A
 * bitola que a média de hoje não tem também não.
 *
 * Com `lista`, a de um elemento na disciplina que mistura (Fase 42, RN-573): a que ele lê, contra
 * a média dele.
 */
export function mudancasDaTaxa(
  disciplina: Pick<CatalogDiscipline, 'services' | 'steelRates' | 'elementSteelRates'>,
  media: ApartmentAverageForProject,
  lista?: AppliesTo,
): readonly MudancaDaTaxa[] {
  const elemento = elementoDoAcoDaLista(disciplina, lista);
  if (elemento === undefined) return [];
  return (steelRatesFor(disciplina, lista) ?? []).flatMap((taxa): MudancaDaTaxa[] => {
    if (taxa.origin !== 'MEDIA') return [];
    const hoje = mediaDaBitola(taxa.gauge, media, elemento);
    if (hoje === undefined || tres(hoje.rate) === tres(taxa.kgPerM3)) return [];
    return [
      {
        gauge: taxa.gauge,
        de: taxa.kgPerM3,
        para: tres(hoje.rate),
        ...(taxa.average === undefined ? {} : { desde: taxa.average.at }),
      },
    ];
  });
}

/**
 * O "Atualizar pela média" da taxa (RN-534; Fase 41, RN-554): a que segue a média passa à de hoje,
 * e a digitada ganha a média nova ao lado, sem mudar. Com `lista`, a de um elemento (Fase 42,
 * RN-573) — que nasce, se ele ainda lia a da disciplina.
 */
export function atualizarTaxaPelaMedia(
  disciplina: Pick<CatalogDiscipline, 'key' | 'services' | 'steelRates' | 'elementSteelRates'>,
  media: ApartmentAverageForProject,
  lista?: AppliesTo,
): readonly WorkspaceAction[] {
  const elemento = elementoDoAcoDaLista(disciplina, lista);
  if (elemento === undefined) return [];
  return (steelRatesFor(disciplina, lista) ?? []).flatMap((taxa): WorkspaceAction[] => {
    const hoje = mediaDaBitola(taxa.gauge, media, elemento);
    if (hoje === undefined) return [];
    const segue = taxa.origin === 'MEDIA';
    if (segue && tres(hoje.rate) === tres(taxa.kgPerM3)) return [];
    return [
      {
        type: 'APPLY_STEEL_AVERAGE',
        disciplineKey: disciplina.key,
        gauge: taxa.gauge,
        average: fotoDaTaxa(hoje, media.at),
        follow: segue,
        ...(lista === undefined ? {} : { element: lista }),
      },
    ];
  });
}
